/** Standardformat einer Erwachsenen-Mannschaft (Bundesliga-typisch): 2×
 * Herrendoppel, 1× Damendoppel, 3× Herreneinzel, 1× Dameneinzel, 1× Mixed —
 * macht die vom Auftraggeber vorgegebenen 8 Spiele. Reihenfolge hier ist
 * zugleich die Anzeigereihenfolge im Frontend. */
export const DEFAULT_DISCIPLINE_ORDER = ['HD', 'DD', 'HE', 'DE', 'MX'];
/** Mini-Mannschaften (§ Nutzeranfrage) spielen ungeschlechtlich und ohne
 * Mixed: 2× Doppel, 4× Einzel — macht die vorgegebenen 6 Spiele. */
export const MINI_DISCIPLINE_ORDER = ['MD', 'ME'];
export const DISCIPLINE_META = {
    HD: { label: 'Herrendoppel', isDoubles: true, instances: 2, genderForMember1: 'm', genderForMember2: 'm' },
    DD: { label: 'Damendoppel', isDoubles: true, instances: 1, genderForMember1: 'w', genderForMember2: 'w' },
    HE: { label: 'Herreneinzel', isDoubles: false, instances: 3, genderForMember1: 'm', genderForMember2: null },
    DE: { label: 'Dameneinzel', isDoubles: false, instances: 1, genderForMember1: 'w', genderForMember2: null },
    MX: { label: 'Mixed', isDoubles: true, instances: 1, genderForMember1: 'm', genderForMember2: 'w' },
    MD: { label: 'Doppel', isDoubles: true, instances: 2, genderForMember1: null, genderForMember2: null },
    ME: { label: 'Einzel', isDoubles: false, instances: 4, genderForMember1: null, genderForMember2: null },
};
/** "M1", "M2", "M3", … irgendwo im Mannschaftsnamen (Wortgrenze davor und
 * danach, sonst würde z. B. "Team12M1" fälschlich matchen) — die vom
 * Auftraggeber vorgegebene Erkennung für Mini-Mannschaften. */
export const MINI_TEAM_NAME_PATTERN = /\bM\d+\b/i;
export function isMiniTeam(teamName) {
    return !!teamName && MINI_TEAM_NAME_PATTERN.test(teamName);
}
export function getLineupFormat(teamName) {
    return isMiniTeam(teamName) ? 'mini' : 'default';
}
export function disciplineOrderFor(format) {
    return format === 'mini' ? MINI_DISCIPLINE_ORDER : DEFAULT_DISCIPLINE_ORDER;
}
export function lineupSlotsFor(format) {
    return disciplineOrderFor(format).flatMap((discipline) => Array.from({ length: DISCIPLINE_META[discipline].instances }, (_, i) => ({ discipline, position: i + 1 })));
}
/** "1. Herrendoppel" bei mehreren Instanzen, sonst nur "Damendoppel" (kein
 * "1." vor einer Disziplin, die nur einmal vorkommt). */
export function describeSlot(discipline, position) {
    const meta = DISCIPLINE_META[discipline];
    return meta.instances > 1 ? `${position}. ${meta.label}` : meta.label;
}
/** member2 hat bei Mixed eine eigene Bezeichnung (Dame statt "Spieler 2"). */
/** Bei ungeschlechtlichen Disziplinen (Mini-Mannschaften, `genderForMember1
 * === null`) gibt es kein "männlich"/"weiblich" zu unterscheiden — dann
 * einheitlich "Spieler" statt einer geratenen Zuordnung. */
export function slotMemberLabel(discipline, memberSlot) {
    const meta = DISCIPLINE_META[discipline];
    if (!meta.isDoubles)
        return meta.genderForMember1 === 'w' ? 'Spielerin' : 'Spieler';
    if (discipline === 'MX')
        return memberSlot === 1 ? 'Herr' : 'Dame';
    return meta.genderForMember1 === 'w' ? `Spielerin ${memberSlot}` : `Spieler ${memberSlot}`;
}
export function requiredGenderForSlotMember(discipline, memberSlot) {
    const meta = DISCIPLINE_META[discipline];
    return memberSlot === 1 ? meta.genderForMember1 : meta.genderForMember2;
}
/** Ein Mitglied ohne gesetztes Geschlecht gilt als zulässig für jede Position
 * — die Regel blockiert nur einen bekannten Konflikt, nie einen unbekannten
 * Zustand (siehe 0026_member_gender.sql-Kommentar). */
export function isGenderEligible(discipline, memberSlot, gender) {
    const required = requiredGenderForSlotMember(discipline, memberSlot);
    if (!required || !gender)
        return true;
    return gender === required;
}
export function genderIneligibleReason(discipline, memberSlot) {
    const meta = DISCIPLINE_META[discipline];
    const noun = requiredGenderForSlotMember(discipline, memberSlot) === 'm' ? 'männliche Spieler' : 'weibliche Spielerinnen';
    return `Nicht verfügbar – nur ${noun} für ${meta.label}${discipline === 'MX' ? ` (Position ${slotMemberLabel(discipline, memberSlot)})` : ''} zugelassen.`;
}
/** Wie oft ein Spieler maximal in einer Aufstellung eingesetzt werden darf —
 * zentral und einmalig definiert (nicht an mehreren Stellen hart codiert),
 * siehe §10 der Anforderung. 2 = ein realistischer Bundesliga-Standard (z. B.
 * ein Doppel + ein Einzel); bei Bedarf später pro Verein/Format konfigurierbar
 * machen (organizationSettings), ohne dass Aufrufer diese Konstante kennen
 * müssten — sie fragen ausschließlich `LINEUP_MAX_APPEARANCES` oder besser
 * `appearanceLimitFor(...)` ab. */
export const LINEUP_MAX_APPEARANCES = 2;
export function appearanceLimitFor(_organizationId) {
    return LINEUP_MAX_APPEARANCES;
}
/** Zählt, in wie vielen Slots ein Mitglied aktuell steht (member1 ODER member2). */
export function countAppearances(slots, memberId) {
    let n = 0;
    for (const s of slots) {
        if (s.member1Id === memberId)
            n += 1;
        if (s.member2Id === memberId)
            n += 1;
    }
    return n;
}
/** Reine Vollständigkeits-/Fehlerprüfung — läuft im Frontend live bei jeder
 * Änderung (kein Server-Roundtrip) UND im Backend defensiv vor dem Speichern
 * (siehe `LineupService.put()`). `genderById` ist optional, damit die
 * Vollständigkeitsprüfung (z. B. für den Status-Badge auf der Saisonspiel-Karte)
 * auch ohne Mitgliederdaten funktioniert — Gender-Verstöße werden dann einfach
 * nicht geprüft, sie sollten ohnehin nie entstehen (UI verhindert sie schon
 * bei der Zuordnung). */
export function validateLineup(slots, genderById, format = 'default') {
    const issues = [];
    const bySlot = new Map(slots.map((s) => [`${s.discipline}:${s.position}`, s]));
    const allSlots = lineupSlotsFor(format);
    let filledSlots = 0;
    for (const { discipline, position } of allSlots) {
        const meta = DISCIPLINE_META[discipline];
        const slot = bySlot.get(`${discipline}:${position}`);
        const need2 = meta.isDoubles;
        const has1 = !!slot?.member1Id;
        const has2 = need2 ? !!slot?.member2Id : true;
        if (has1 && has2)
            filledSlots += 1;
        else {
            issues.push({
                code: 'missing_slot',
                discipline,
                position,
                message: `${describeSlot(discipline, position)}${need2 ? (has1 ? ` — ${slotMemberLabel(discipline, 2)} fehlt` : ' nicht vollständig') : ' fehlt'}`,
            });
        }
        if (need2 && slot?.member1Id && slot.member2Id && slot.member1Id === slot.member2Id) {
            issues.push({
                code: 'duplicate_in_slot',
                discipline,
                position,
                message: `${describeSlot(discipline, position)}: derselbe Spieler kann nicht beide Positionen besetzen.`,
            });
        }
        if (genderById && slot) {
            for (const memberSlot of [1, 2]) {
                const memberId = memberSlot === 1 ? slot.member1Id : slot.member2Id;
                if (!memberId)
                    continue;
                const gender = genderById.get(memberId) ?? null;
                if (!isGenderEligible(discipline, memberSlot, gender)) {
                    issues.push({
                        code: 'wrong_gender',
                        discipline,
                        position,
                        memberSlot,
                        message: `${describeSlot(discipline, position)}: ${genderIneligibleReason(discipline, memberSlot)}`,
                    });
                }
            }
        }
    }
    if (genderById) {
        const allMemberIds = new Set(slots.flatMap((s) => [s.member1Id, s.member2Id].filter((x) => !!x)));
        for (const memberId of allMemberIds) {
            const count = countAppearances(slots, memberId);
            if (count > LINEUP_MAX_APPEARANCES) {
                issues.push({
                    code: 'too_many_appearances',
                    memberId,
                    message: `Ein Spieler ist ${count}× eingeplant, maximal zulässig sind ${LINEUP_MAX_APPEARANCES}.`,
                });
            }
        }
    }
    const status = filledSlots === 0 && slots.every((s) => !s.member1Id && !s.member2Id)
        ? 'empty'
        : filledSlots === allSlots.length
            ? 'complete'
            : 'incomplete';
    return { status, filledSlots, totalSlots: allSlots.length, issues };
}
export const LINEUP_STATUS_LABELS = {
    empty: 'nicht erstellt',
    incomplete: 'unvollständig',
    complete: 'vollständig',
};
//# sourceMappingURL=lineup-rules.js.map